import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
 Bell,
 Check,
 CheckCheck,
 Trash2,
 Loader2,
 ListChecks,
 Calendar,
 CheckCircle,
 XCircle,
 Play,
 Flag,
 DollarSign,
 Clock,
 Star,
 MessageSquare,
 Megaphone,
 Settings,
 Film,
} from 'lucide-react';
import {
 useNotifications,
 useMarkNotificationAsRead,
 useMarkAllNotificationsAsRead,
 useDeleteNotification,
} from '../../services/notificationService';
import { useToastContext } from '../../contexts/ToastContext';
import Button from '../../components/shared/Button';
import { VendorPageHeader, VendorGuideCallout } from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';

const FILTER_TABS = [
 { key: 'all', label: 'All' },
 { key: 'unread', label: 'Unread' },
 { key: 'read', label: 'Read' },
];

const VendorNotifications = () => {
 const navigate = useNavigate();
 const { showToast } = useToastContext();
 const [filter, setFilter] = useState('all'); // all, unread, read

 const { data: notificationsData, isLoading } = useNotifications({ per_page: 50 });
 
 // Handle the API response structure - data is inside data.data for paginated responses
 const notifications = notificationsData?.data?.data || notificationsData?.data || [];

 const markAsReadMutation = useMarkNotificationAsRead({
 onSuccess: () => {
 showToast('Notification marked as read', 'success');
 },
 });

 const markAllAsReadMutation = useMarkAllNotificationsAsRead({
 onSuccess: () => {
 showToast('All notifications marked as read', 'success');
 },
 });

 const deleteMutation = useDeleteNotification({
 onSuccess: () => {
 showToast('Notification deleted', 'success');
 },
 });

 const filteredNotifications = Array.isArray(notifications) ? notifications.filter(notif => {
 if (filter === 'unread') return !notif.is_read;
 if (filter === 'read') return notif.is_read;
 return true;
 }) : [];

 const unreadCount = Array.isArray(notifications) ? notifications.filter(n => !n.is_read).length : 0;
 const readCount = Array.isArray(notifications) ? notifications.length - unreadCount : 0;

 const handleMarkAsRead = (notificationId) => {
 markAsReadMutation.mutate(notificationId);
 };

 const handleMarkAllAsRead = () => {
 if (unreadCount > 0) {
 markAllAsReadMutation.mutate();
 }
 };

 const handleDelete = (notificationId) => {
 if (window.confirm('Are you sure you want to delete this notification?')) {
 deleteMutation.mutate(notificationId);
 }
 };

 const getNotificationIcon = (type) => {
 switch (type) {
 case 'booking_request':
 return {
 Icon: Calendar,
 color: 'text-ink',
 bg: 'bg-surface-muted border-border ',
 };
 case 'external_lead':
 case 'consultation_lead':
 return {
 Icon: MessageSquare,
 color: 'text-ink',
 bg: 'bg-surface-muted border-border ',
 };
 case 'booking_confirmed':
 return {
 Icon: CheckCircle,
 color: 'text-emerald-600',
 bg: 'bg-emerald-50 border-emerald-100',
 };
 case 'booking_cancelled':
 return {
 Icon: XCircle,
 color: 'text-red-600',
 bg: 'bg-red-50 border-red-100',
 };
 case 'booking_in_progress':
 return {
 Icon: Play,
 color: 'text-ink',
 bg: 'bg-surface-muted border-border ',
 };
 case 'booking_completed':
 return {
 Icon: Flag,
 color: 'text-emerald-600',
 bg: 'bg-emerald-50 border-emerald-100',
 };
 case 'payment_received':
 return {
 Icon: DollarSign,
 color: 'text-emerald-600',
 bg: 'bg-emerald-50 border-emerald-100',
 };
 case 'payment_pending':
 return {
 Icon: Clock,
 color: 'text-amber-600',
 bg: 'bg-amber-50 border-amber-100',
 };
 case 'event_started':
 return {
 Icon: Film,
 color: 'text-ink',
 bg: 'bg-surface-muted border-border ',
 };
 case 'event_ended':
 return {
 Icon: Flag,
 color: 'text-ink-secondary',
 bg: 'bg-gray-100 border-border',
 };
 case 'review_received':
 return {
 Icon: Star,
 color: 'text-amber-600',
 bg: 'bg-amber-50 border-amber-100',
 };
 case 'message_received':
 return {
 Icon: MessageSquare,
 color: 'text-ink',
 bg: 'bg-surface-muted border-border ',
 };
 case 'broadcast':
 return {
 Icon: Megaphone,
 color: 'text-ink',
 bg: 'bg-surface-muted border-border ',
 };
 case 'reminder':
 return {
 Icon: Clock,
 color: 'text-amber-600',
 bg: 'bg-amber-50 border-amber-100',
 };
 case 'system':
 return {
 Icon: Settings,
 color: 'text-ink-secondary',
 bg: 'bg-gray-100 border-border',
 };
 default:
 return {
 Icon: Bell,
 color: 'text-ink-secondary',
 bg: 'bg-gray-100 border-border',
 };
 }
 };

 const formatTimeAgo = (dateString) => {
 const date = new Date(dateString);
 const now = new Date();
 const seconds = Math.floor((now - date) / 1000);
 
 if (seconds < 60) return 'just now';
 if (seconds < 3600) return `${Math.floor(seconds / 60)} minute${Math.floor(seconds / 60) > 1 ? 's' : ''} ago`;
 if (seconds < 86400) return `${Math.floor(seconds / 3600)} hour${Math.floor(seconds / 3600) > 1 ? 's' : ''} ago`;
 if (seconds < 604800) return `${Math.floor(seconds / 86400)} day${Math.floor(seconds / 86400) > 1 ? 's' : ''} ago`;
 return date.toLocaleDateString();
 };

 const getFilterCount = (key) => {
 if (key === 'unread') return unreadCount;
 if (key === 'read') return readCount;
 return Array.isArray(notifications) ? notifications.length : 0;
 };

 const emptyCopy =
 filter === 'unread'
 ? "You're all caught up — no unread notifications."
 : filter === 'read'
 ? 'No read notifications yet.'
 : "You don't have any notifications yet.";

 return (
 <div className="space-y-5">
 <VendorPageHeader
 title="Notifications"
 subtitle={
 unreadCount > 0
 ? `Booking, payment, and support alerts — ${unreadCount} unread.`
 : 'Booking, payment, and support alerts land here so you never miss a request.'
 }
 actions={
 unreadCount > 0 ? (
 <Button
 onClick={handleMarkAllAsRead}
 icon={CheckCheck}
 variant="outline"
 className="rounded-md"
 disabled={markAllAsReadMutation.isPending}
 loading={markAllAsReadMutation.isPending}
 >
 Mark All as Read
 </Button>
 ) : null
 }
 />

 <VendorGuideCallout
 title={VENDOR_PAGE_GUIDES.notifications.title}
 steps={VENDOR_PAGE_GUIDES.notifications.steps}
 icon={ListChecks}
 />

 <div className="rounded-lg border border-border bg-surface shadow-sm overflow-hidden">
 <div className="border-b border-border px-4 py-3 sm:px-5">
 <div
 className="inline-flex flex-wrap rounded-xl bg-gray-100 p-1 border border-border"
 role="tablist"
 aria-label="Notification filter"
 >
 {FILTER_TABS.map((tab) => {
 const isActive = filter === tab.key;
 return (
 <button
 key={tab.key}
 type="button"
 role="tab"
 aria-selected={isActive}
 onClick={() => setFilter(tab.key)}
 className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${ isActive ? 'bg-surface text-ink shadow-sm' : 'text-ink-secondary hover:text-gray-900' }`}
 >
 {tab.label}
 <span
 className={`ml-1.5 tabular-nums ${ isActive ? 'text-ink-tertiary' : 'text-gray-400' }`}
 >
 ({getFilterCount(tab.key)})
 </span>
 </button>
 );
 })}
 </div>
 </div>

 <div className="p-4 sm:p-5">
 {isLoading ? (
 <div className="py-20 text-center">
 <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto mb-3" />
 <p className="text-sm text-ink-tertiary">Loading notifications…</p>
 </div>
 ) : filteredNotifications.length === 0 ? (
 <div className="py-16 sm:py-20 text-center px-4">
 <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-lg border border-border bg-surface-muted text-gray-400">
 <Bell className="w-7 h-7" />
 </div>
 <h3 className="text-base font-semibold text-ink">
 No notifications
 </h3>
 <p className="mt-1.5 text-sm text-ink-tertiary max-w-md mx-auto leading-relaxed">
 {emptyCopy}
 </p>
 </div>
 ) : (
 <div className="space-y-3">
 {filteredNotifications.map((notification) => {
 const { Icon, color, bg } = getNotificationIcon(notification.type);
 return (
 <div
 key={notification.id}
 className={`rounded-xl border p-4 sm:p-5 transition-colors ${ !notification.is_read ? 'border-border/80 bg-surface-muted/40 hover:border-border' : 'border-border bg-surface-muted hover:border-ink' } ${notification.is_broadcast || notification.data?.href ? 'cursor-pointer' : ''}`}
 onClick={() => {
 if (notification.is_broadcast) {
 window.dispatchEvent(
 new CustomEvent('vendor:showBroadcastModal', {
 detail: { notification },
 })
 );
 return;
 }
 const href = notification.data?.href;
 if (href) {
 if (!notification.is_read) {
 markAsReadMutation.mutate(notification.id);
 }
 navigate(href);
 }
 }}
 >
 <div className="flex items-start gap-3.5 sm:gap-4">
 <div
 className={`flex-shrink-0 w-10 h-10 rounded-xl border flex items-center justify-center ${bg}`}
 >
 <Icon className={`w-[18px] h-[18px] ${color}`} />
 </div>
 <div className="flex-1 min-w-0">
 <div className="flex items-start justify-between gap-3">
 <div className="flex-1 min-w-0">
 <div className="flex flex-wrap items-center gap-2 mb-1">
 <h3 className="text-sm font-semibold text-ink">
 {notification.title || 'Notification'}
 </h3>
 {notification.is_broadcast && (
 <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium border border-border bg-surface-muted text-ink">
 Broadcast
 </span>
 )}
 {!notification.is_read && (
 <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium border border-border bg-surface text-ink-secondary">
 New
 </span>
 )}
 </div>
 <p className="text-sm text-ink-secondary mb-2 leading-relaxed">
 {notification.message}
 </p>
 <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-tertiary">
 <span>{formatTimeAgo(notification.created_at)}</span>
 <span className="capitalize text-gray-400">
 {notification.type?.replace(/_/g, ' ')}
 </span>
 </div>
 </div>
 <div
 className="flex items-center gap-0.5 flex-shrink-0"
 onClick={(e) => e.stopPropagation()}
 >
 {!notification.is_read && (
 <button
 type="button"
 onClick={() => handleMarkAsRead(notification.id)}
 disabled={markAsReadMutation.isPending}
 className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors disabled:opacity-50"
 title="Mark as read"
 >
 <Check className="w-4 h-4" />
 </button>
 )}
 <button
 type="button"
 onClick={() => handleDelete(notification.id)}
 disabled={deleteMutation.isPending}
 className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
 title="Delete notification"
 >
 <Trash2 className="w-4 h-4" />
 </button>
 </div>
 </div>
 </div>
 </div>
 </div>
 );
 })}
 </div>
 )}
 </div>
 </div>
 </div>
 );
};

export default VendorNotifications;
